import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { subscribeAllEntitiesResolver } from '@adornis/baseql/server/resolver/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { gateAnyNexusSubscription, gateNexusSubscription } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { PartnerSchedulingAllowedQueue } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingAllowedQueue.js';
import { PartnerSchedulingClientRecord } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingClientRecord.js';
import { PartnerSchedulingClientStatus } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingClientStatus.js';
import { PartnerSchedulingPartner } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingPartner.js';
import { of, from, map, switchMap, type Observable } from 'rxjs';

const PARTNER_SCHEDULING_STATUS_READ = [
  NexusPermission.PartnerSchedulingClientToggle,
  NexusPermission.PartnerSchedulingImport,
  NexusPermission.UserManage,
] as const;

/** Sites / counts / partners — operators only (not UserManage). */
const PARTNER_SCHEDULING_DATA_READ = [
  NexusPermission.PartnerSchedulingClientToggle,
  NexusPermission.PartnerSchedulingImport,
] as const;

/** Aggregated workbook site counts per client (not persisted). */
@Entity()
export class PartnerSchedulingClientSiteCount extends AdornisEntity {
  static override _class = 'PartnerSchedulingClientSiteCount';

  static override resolveID(entity: PartnerSchedulingClientSiteCount): string {
    return entity.clientKey ?? '';
  }

  @Field(() => String)
  clientKey!: string;

  @Field(() => Int)
  siteCount: Maybe<number>;
}

function gatePartnerSchedulingStatusRead<T>(inner: () => Observable<T>): Observable<T> {
  return gateAnyNexusSubscription([...PARTNER_SCHEDULING_STATUS_READ], inner);
}

function gatePartnerSchedulingDataRead<T>(inner: () => Observable<T>): Observable<T> {
  return gateAnyNexusSubscription([...PARTNER_SCHEDULING_DATA_READ], inner);
}

function gatePartnerImportRead<T>(entityClass: string, gqlFields: BaseQLSelectionSet<T>) {
  return gateNexusSubscription(NexusPermission.PartnerSchedulingImport, () =>
    subscribeAllEntitiesResolver(entityClass)(gqlFields),
  );
}

async function loadClientSiteCounts(): Promise<PartnerSchedulingClientSiteCount[]> {
  const col = await getRawCollection<EntityData<PartnerSchedulingClientRecord>>(
    PartnerSchedulingClientRecord._collectionName,
  );
  const rows = await col
    .aggregate<{ _id: string; siteCount: number }>([{ $group: { _id: '$clientKey', siteCount: { $sum: 1 } } }])
    .toArray();
  return rows
    .filter(row => row._id)
    .map(
      row =>
        new PartnerSchedulingClientSiteCount({
          clientKey: String(row._id),
          siteCount: row.siteCount ?? 0,
        }),
    );
}

function loadClientRecords(docs: EntityData<PartnerSchedulingClientRecord>[]): PartnerSchedulingClientRecord[] {
  return docs.map(
    doc =>
      new PartnerSchedulingClientRecord({
        ...doc,
        _class: doc._class ?? PartnerSchedulingClientRecord._class,
      }),
  );
}

function loadPartners(docs: EntityData<PartnerSchedulingPartner>[]): PartnerSchedulingPartner[] {
  return docs.map(
    doc =>
      new PartnerSchedulingPartner({
        ...doc,
        _class: doc._class ?? PartnerSchedulingPartner._class,
      }),
  );
}

/** Small aggregated payload instead of watching all workbook site rows. */
export const subscribePartnerSchedulingClientSiteCounts = registerSubscription({
  operationName: 'subscribePartnerSchedulingClientSiteCounts',
  type: () => [PartnerSchedulingClientSiteCount],
  params: [],
  resolve: () => (_gqlFields: BaseQLSelectionSet<PartnerSchedulingClientSiteCount>) =>
    gatePartnerSchedulingDataRead(() =>
      watchEntireCollection(PartnerSchedulingClientRecord._collectionName).pipe(
        switchMap(() => from(loadClientSiteCounts())),
      ),
    ),
});

/** Workbook sites for one client only (modal). */
export const subscribePartnerSchedulingClientSitesForClient = registerSubscription({
  operationName: 'subscribePartnerSchedulingClientSitesForClient',
  type: () => [PartnerSchedulingClientRecord],
  params: [{ name: 'clientKey', type: () => String }],
  resolve: (clientKey: string) => (_gqlFields: BaseQLSelectionSet<PartnerSchedulingClientRecord>) =>
    gatePartnerSchedulingDataRead(() => {
      const trimmed = clientKey.trim();
      if (!trimmed) return of([]);
      return watchQuery<EntityData<PartnerSchedulingClientRecord>>(
        PartnerSchedulingClientRecord._collectionName,
        { clientKey: trimmed },
      ).pipe(map(loadClientRecords));
    }),
});

/** Partner contacts referenced by the open client modal only. */
export const subscribePartnerSchedulingPartnersForNames = registerSubscription({
  operationName: 'subscribePartnerSchedulingPartnersForNames',
  type: () => [PartnerSchedulingPartner],
  params: [{ name: 'partnerNames', type: () => [String] }],
  resolve: (partnerNames: string[] | undefined) => (_gqlFields: BaseQLSelectionSet<PartnerSchedulingPartner>) =>
    gatePartnerSchedulingDataRead(() => {
      const names = [...new Set((partnerNames ?? []).map(name => name.trim()).filter(Boolean))];
      if (names.length === 0) return of([]);
      return watchQuery<EntityData<PartnerSchedulingPartner>>(PartnerSchedulingPartner._collectionName, {
        partnerName: { $in: names },
      }).pipe(map(loadPartners));
    }),
});

export const subscribePartnerSchedulingClientStatus = registerSubscription({
  operationName: 'subscribePartnerSchedulingClientStatus',
  type: () => [PartnerSchedulingClientStatus],
  params: [],
  resolve: () => (gqlFields: BaseQLSelectionSet<PartnerSchedulingClientStatus>) =>
    gatePartnerSchedulingStatusRead(() => subscribeAllEntitiesResolver(PartnerSchedulingClientStatus._class)(gqlFields)),
});

export const subscribePartnerSchedulingAllowedQueues = registerSubscription({
  operationName: 'subscribePartnerSchedulingAllowedQueues',
  type: () => [PartnerSchedulingAllowedQueue],
  params: [],
  resolve: () => (gqlFields: BaseQLSelectionSet<PartnerSchedulingAllowedQueue>) =>
    gatePartnerImportRead(PartnerSchedulingAllowedQueue._class, gqlFields),
});
